import { Button } from "@/components/ui/button";
import { formatApprovalDateDisplay } from "@/lib/proposalCustomerActions";
import VoContactConsultantBar from "./VoContactConsultantBar";

const SALES_TEXT = "#1A2744";
const SALES_ACCENT = "#C8A96A";

export const VO_STAFF_APPROVAL_DISABLED_HELPER =
  "Approval can only be completed from the customer link (Link B). This staff preview shows the same controls the customer will see.";

export type VoApprovalSectionProps = {
  status: string;
  approvedAt?: string | Date | null;
  approvedEmail?: string | null;
  /** Staff Link A — same layout, permanently non-interactive. */
  approvalDisabled?: boolean;
  emailDeliveryFailed?: boolean;
  approvePending?: boolean;
  onApproveClick?: () => void;
  /** Optional consultant contact action for the shared bar (WhatsApp / mailto). */
  consultantContactHref?: string | null;
};

/**
 * Shared VO approval chrome for customer Link B and staff Link A preview.
 * Does not perform approval — callers wire mutations / disable staff actions.
 */
export default function VoApprovalSection({
  status,
  approvedAt,
  approvedEmail,
  approvalDisabled = false,
  emailDeliveryFailed = false,
  approvePending = false,
  onApproveClick,
  consultantContactHref = null,
}: VoApprovalSectionProps) {
  const normalized = String(status ?? "").toLowerCase();
  const isApproved = normalized === "approved";
  const isRejected = normalized === "rejected";
  const isCancelled = normalized === "cancelled";

  const showDecision =
    !isApproved &&
    !isRejected &&
    !isCancelled &&
    (approvalDisabled ? normalized === "draft" || normalized === "sent" : normalized === "sent");

  const approvedBanner = isApproved ? (
    <div
      className="w-full space-y-2 rounded-lg border border-[#C8A96A]/40 bg-[#FAFAF7] p-4 text-sm"
      data-testid="vo-public-approved-banner"
    >
      <p className="font-semibold" style={{ color: SALES_ACCENT }}>
        Approved
      </p>
      <p style={{ color: SALES_TEXT }}>
        Approved on <span className="font-medium">{formatApprovalDateDisplay(approvedAt)}</span>
      </p>
      {String(approvedEmail ?? "").trim() ? (
        <p style={{ color: SALES_TEXT }}>
          Confirmation sent to <span className="font-medium">{String(approvedEmail).trim()}</span>
        </p>
      ) : null}
      {emailDeliveryFailed ? (
        <p className="text-amber-900" data-testid="vo-approval-email-failed">
          Your approval was recorded, but the confirmation email could not be sent. Please contact
          your consultant if you need a copy of the approved Variation Order.
        </p>
      ) : null}
      <p className="text-[#1A2744]/70">
        You are viewing your confirmed Variation Order in read-only mode.
      </p>
    </div>
  ) : null;

  const rejectedBanner = isRejected ? (
    <p
      className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
      data-testid="vo-public-rejected-banner"
    >
      This Variation Order was rejected. Your consultant has been notified.
    </p>
  ) : null;

  const decisionPanel = showDecision ? (
    <div
      className="w-full space-y-4 rounded-xl border border-[#1A2744]/12 bg-white p-4"
      data-testid="vo-customer-decision"
    >
      <p className="text-sm text-[#1A2744]/75">Review this Variation Order, then approve below.</p>
      <Button
        type="button"
        className="h-auto min-h-11 w-full bg-[#1A2744] text-white hover:bg-[#1A2744]/90 disabled:opacity-60"
        onClick={() => {
          if (approvalDisabled) return;
          onApproveClick?.();
        }}
        disabled={approvalDisabled || approvePending}
        data-testid="vo-approve-open"
      >
        Approve Variation Order
      </Button>
      {approvalDisabled ? (
        <p className="text-xs text-muted-foreground" data-testid="vo-staff-approval-disabled-hint">
          {VO_STAFF_APPROVAL_DISABLED_HELPER}
        </p>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="w-full space-y-4" data-testid="vo-approval-and-contact">
      {approvedBanner}
      {rejectedBanner}
      {decisionPanel}
      <VoContactConsultantBar contactHref={consultantContactHref} />
    </div>
  );
}
